import {
  At,
  Bell,
  BellSlash,
  Hash,
  Handshake,
  Lightning,
  PushPin,
  Rows,
  Terminal,
  User,
  UserPlus,
  VideoCamera,
  Users,
} from "@/components/ui/icons";
import { AnimatedImage } from "@/components/ui/animated-image";
import { Button } from "@/components/ui/button";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { PANEL_DIVIDER_BORDER_STYLE } from "@/lib/surface-styles";
import { cn } from "@/lib/utils";
import { canUseShellTerminal, isElectronRuntime } from "@/lib/logout";
import { openDesktopDownload } from "@/lib/desktop-download";

interface MessageAreaHeaderProps {
  channel:
    | {
        topic?: string | null;
        is_archived?: boolean;
      }
    | null
    | undefined;
  channelMemberListCount: number;
  displayName: string | null | undefined;
  isDM: boolean;
  isCoworkerDm: boolean;
  hasCollaborators: boolean;
  dmHeaderOtherUserId: string | null;
  dmHeaderAvatarUrl: string | null | undefined;
  workspaceId: string | null;
  callsEnabled: boolean;
  isCallActionDisabled: boolean;
  visibleChannelPinsCount: number;
  showPinnedMessages: boolean;
  showControlTower: boolean;
  canToggleMute?: boolean;
  isMuted?: boolean;
  hasMentionInbox?: boolean;
  showMentionInbox?: boolean;
  onSelectProfile: (userId: string) => void;
  onJoinCall: (mode: "audio" | "video", channelName: string) => void;
  onToggleTerminal?: () => void;
  /** Mobile only — opens the Sidebar drawer. Hamburger button is
   *  `md:hidden` so it never shows on desktop. */
  onOpenMobileSidebar?: () => void;
  onToggleMute?: () => void;
  onTogglePinned: () => void;
  onToggleControlTower: () => void;
  onToggleMembers: () => void;
  onToggleMentionInbox?: () => void;
  onInviteToConversation: () => void;
}

/**
 * The horizontal title bar at the top of MessageArea: avatar/icon +
 * channel name + topic + action buttons (audio/video/terminal/pinned/
 * invite/members).
 */
export function MessageAreaHeader({
  channel,
  channelMemberListCount,
  displayName,
  isDM,
  isCoworkerDm,
  hasCollaborators,
  dmHeaderOtherUserId,
  dmHeaderAvatarUrl,
  workspaceId,
  callsEnabled,
  isCallActionDisabled,
  visibleChannelPinsCount,
  showPinnedMessages,
  showControlTower,
  canToggleMute,
  isMuted,
  hasMentionInbox,
  showMentionInbox,
  onSelectProfile,
  onJoinCall,
  onToggleTerminal,
  onOpenMobileSidebar,
  onToggleMute,
  onTogglePinned,
  onToggleControlTower,
  onToggleMembers,
  onToggleMentionInbox,
  onInviteToConversation,
}: MessageAreaHeaderProps) {
  return (
    <div
      className="relative z-20 flex h-12 min-w-0 items-center gap-2 border-b px-4 @container"
      style={PANEL_DIVIDER_BORDER_STYLE}
      data-message-area-header
    >
      {/* Mobile-only hamburger that opens the Sidebar drawer. Hidden at
          `md:` and above where the Sidebar is a permanent flex column. */}
      {onOpenMobileSidebar && (
        <IconTooltip label="Open sidebar">
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenMobileSidebar}
            aria-label="Open sidebar"
            className="md:hidden -ml-2 h-8 w-8 shrink-0 p-1.5 text-foreground/80"
          >
            <Rows size={18} />
          </Button>
        </IconTooltip>
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (dmHeaderOtherUserId) onSelectProfile(dmHeaderOtherUserId);
          }}
          disabled={!dmHeaderOtherUserId}
          className={cn(
            "flex h-auto w-full max-w-full min-w-0 items-center justify-start gap-2 overflow-hidden px-1 py-0",
            dmHeaderOtherUserId
              ? "-ml-1 hover:bg-foreground/5"
              : "cursor-default hover:bg-transparent",
          )}
          title={
            dmHeaderOtherUserId
              ? "View profile"
              : channel?.topic && !isDM
                ? channel.topic
                : undefined
          }
        >
          {isDM ? (
            dmHeaderAvatarUrl ? (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-foreground/20">
                <AnimatedImage
                  src={dmHeaderAvatarUrl}
                  alt={displayName || ""}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <User size={16} className="shrink-0" />
            )
          ) : (
            <Hash size={16} className="shrink-0" />
          )}
          <span className="shrink-0 font-semibold whitespace-nowrap">
            {displayName || "Loading..."}
          </span>
          {isCoworkerDm && <CoworkerIcon size={12} className="shrink-0" />}
          {hasCollaborators && <Handshake size={14} className="shrink-0" />}
          {channel?.topic && !isDM && (
            <span className="min-w-0 flex-1 truncate border-l border-border pl-2 text-xs text-muted-foreground">
              {channel.topic}
            </span>
          )}
        </Button>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {callsEnabled && workspaceId && !channel?.is_archived && (
          <IconTooltip label="Join video call">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onJoinCall("video", displayName || "channel")}
              disabled={isCallActionDisabled}
              aria-label="Join video call"
              className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground disabled:opacity-50"
            >
              <VideoCamera size={16} className="shrink-0" />
              <span className="hidden @lg:inline">Video</span>
            </Button>
          </IconTooltip>
        )}
        {canToggleMute && onToggleMute && !channel?.is_archived && (
          <IconTooltip label={isMuted ? "Unmute channel" : "Mute channel"}>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleMute}
              aria-label={isMuted ? "Unmute channel" : "Mute channel"}
              className="p-1.5 hover:bg-foreground/10"
            >
              {isMuted ? (
                <BellSlash size={16} className="text-foreground/80" />
              ) : (
                <Bell size={16} className="text-foreground/80" />
              )}
            </Button>
          </IconTooltip>
        )}
        {onToggleTerminal && canUseShellTerminal() && (
          <IconTooltip label="Toggle terminal (⌘J)">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleTerminal}
              aria-label="Toggle terminal"
              className="p-1.5 hover:bg-foreground/10"
            >
              <Terminal size={16} className="text-foreground/80" />
            </Button>
          </IconTooltip>
        )}
        {!isElectronRuntime() && (
          <IconTooltip label="The Shell runs in the desktop app — download to use it">
            <Button
              variant="ghost"
              size="sm"
              onClick={openDesktopDownload}
              className="hidden md:flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground"
            >
              <Terminal size={14} className="shrink-0" />
              <span className="hidden @lg:inline">Get desktop</span>
            </Button>
          </IconTooltip>
        )}
        {hasMentionInbox && onToggleMentionInbox && (
          <IconTooltip label="Toggle mention inbox">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleMentionInbox}
              aria-label="Toggle mention inbox"
              className="p-1.5 hover:bg-foreground/10"
            >
              <At
                size={16}
                weight={showMentionInbox ? "Bold" : "Linear"}
                className="text-foreground/80"
              />
            </Button>
          </IconTooltip>
        )}
        {visibleChannelPinsCount > 0 && (
          <IconTooltip label="View pinned messages">
            <Button
              variant="ghost"
              size="sm"
              onClick={onTogglePinned}
              className="flex items-center gap-1 p-1.5 text-xs text-foreground/80"
            >
              <PushPin
                size={16}
                weight={showPinnedMessages ? "Bold" : "Linear"}
              />
              <span className="hidden @md:inline">
                {visibleChannelPinsCount}
              </span>
            </Button>
          </IconTooltip>
        )}
        {isDM && !channel?.is_archived && (
          <IconTooltip label="Invite to conversation">
            <Button
              variant="ghost"
              size="icon"
              onClick={onInviteToConversation}
              aria-label="Invite to conversation"
              className="p-1.5 hover:bg-foreground/10 text-foreground/80"
            >
              <UserPlus size={16} />
            </Button>
          </IconTooltip>
        )}
        <IconTooltip label="Ano Sync · Control Tower">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleControlTower}
            aria-label="Toggle Control Tower"
            className="flex items-center gap-1 p-1.5 text-xs text-foreground/80"
          >
            <Lightning
              size={16}
              weight={showControlTower ? "Bold" : "Linear"}
              className={showControlTower ? "text-info" : undefined}
            />
          </Button>
        </IconTooltip>
        <IconTooltip label="Show members">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleMembers}
            className="flex items-center gap-1 p-1.5 text-xs text-foreground/80"
          >
            <Users size={16} />
            <span className="hidden @md:inline">{channelMemberListCount}</span>
          </Button>
        </IconTooltip>
      </div>
    </div>
  );
}
